import { hubConnection } from '../../vendor/signalR';
import { Connection } from '../lib/Connection';
import { eventTypes, logLevel, messageSources, statuses } from '../lib/enums';

const defaultConfig = {
	reconnectDelay: 5 * 1000,
	reconnectAttempts: 5,
	logLevel: null,
};

/**
 * Initialize (but don't start) a SignalR connection
 * @param {string} url - The URL to which to connect
 * @param {string} hubName - The name of the SignalR hub to which to connect
 * @param {string} userId - The ID of the current user
 * @param {object} [config] - Configuration settings for the connection
 * @returns {Connection} The new connection
 */
export const CreateConnection = (url, hubName, userId, _config = {}) => {
	const config = Object.assign({}, defaultConfig, _config);

	const connectionConfig = {
		logLevel: null,
	};

	if (config.logLevel !== undefined && config.logLevel !== null) {
		connectionConfig.logLevel = config.logLevel;
	}

	const connection = new Connection(connectionConfig);

	connection.log(logLevel.force, `Connection initialized to ${hubName} @ ${url} as user id ${userId}`);

	const signalRConnection = hubConnection(url, { qs: { userGuid: userId } });
	const hubProxy = signalRConnection.createHubProxy(hubName);

	// use signalr connection to build our connection object

	//override start and stop methods
	connection.start = () => startConnection(connection, signalRConnection);

	connection.stop = () => {
		connection.log(logLevel.info, 'Stopping');

		connection.setStatus(statuses.STOPPED);
		signalRConnection.stop();
	};

	// map signalr events
	hubProxy.on('message', (message) => handleMessage(message, connection));

	signalRConnection.disconnected(() => {
		connection.log(logLevel.info, 'Disconnected');

		if (connection.status !== statuses.STOPPED && (config.reconnectAttempts === -1 || connection.reconnectAttempts < config.reconnectAttempts)) {
			// auto reconnect
			connection.setStatus(statuses.DISCONNECTED);

			setTimeout(() => startConnection(connection, signalRConnection), config.reconnectDelay);

			connection.reconnectAttempts += 1;
		} else {
			connection.log(logLevel.info, 'Stopped');

			connection.setStatus(statuses.STOPPED);
		}

		connection.fire(eventTypes.disconnected);
	});

	signalRConnection.reconnected(() => {
		connection.log(logLevel.info, 'Reconnected');

		connection.setStatus(statuses.CONNECTED);
		connection.fire(eventTypes.reconnected);
	});

	signalRConnection.error((error) => {
		if (!error) {
			// it seems like the only time this event gets called without an error is
			// on connection fail, which we already have a listener for
			return;
		}

		connection.log(logLevel.error, 'Error: \n', error);

		connection.fire(eventTypes.error, error);
	});

	// return connection object
	return connection;
};

const startConnection = (connection, signalRConnection) => {
	connection.log(logLevel.info, 'Connecting');

	const promise = signalRConnection.start();
	connection.setStatus(statuses.CONNECTING);

	promise.done(() => {
		connection.log(logLevel.info, 'Connected');

		connection.setStatus(statuses.CONNECTED);
		connection.fire(eventTypes.connected);
		connection.reconnectAttempts = 0;
	});

	promise.fail((...args) => {
		connection.log(logLevel.error, 'Unable to connect \n', ...args);

		connection.setStatus(statuses.ERRORED);
		connection.fire(eventTypes.error, ...args);
	});

	return promise;
};

const handleMessage = (message, connection) => {
	connection.log(logLevel.trace, 'Message received :\n', JSON.stringify(message, null, '  '));

	switch (message.Source) {
		case messageSources.progress:
			connection.fire(eventTypes.progress, message);
			break;
		case messageSources.notification:
			connection.fire(eventTypes.notification, message);
			break;
		default:
			connection.fire(eventTypes.message, message);
			break;
	}
};
